// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import {
  AniseikoniaHalfViewType,
  AniseikoniaTargetGeometryType,
  AniseikoniaViewType,
} from "src/services/exams/types";

// How much larger than life the size difference is drawn at. 2% of a 2 degree feature is
// 0.04 degrees -- well under a pixel at any sensible figure size -- so the difference has to be
// exaggerated to be visible at all. The caption always says so, and both halves are exaggerated
// by the same factor so the picture stays honest about which eye saw the larger image.
export const DEFAULT_EXAGGERATION = 14;

const FIGURE_SIZE = 180;

type TargetFigureType = {
  geometry: AniseikoniaTargetGeometryType | undefined;
  view: AniseikoniaViewType | null | undefined;
  // Which stage's unscaled extents to exaggerate against: a bar is slender across the axis it is
  // not judged on, so with a bar pair the unscaled feature is 2.0 x 0.7 horizontally and
  // 0.7 x 2.0 vertically.
  meridian: "horizontal" | "vertical" | "overall";
  caption?: string;
  exaggeration?: number;
  size?: number;
  // The lock carries no difference, so it is optional -- but it is what the patient was holding
  // fusion on, and drawing it makes the two halves read as one scene rather than two floating
  // shapes.
  showLock?: boolean;
};

/**
 * Draws one presentation exactly as the renderer was given it.
 *
 * Every length in `geometry` and `view` is in degrees of visual angle, and `fieldExtentDegrees`
 * is HALF the width of the square field -- so mapping it to half the canvas puts every other
 * number into pixels with the same factor.
 *
 * This reads `view` rather than a percentage on purpose. Two conversions sit between them and
 * both are easy to get backwards: the report's `percent` is the patient's aniseikonia while
 * `controlPercent` is where the control sat, and they have opposite signs; and a setting of `p`
 * scales the right half by sqrt(1 + p/100) and the left by its reciprocal, so the pair's mean
 * size stays fixed while the patient drives. `view` has both applied already.
 */
const TargetFigure = ({
  geometry,
  view,
  meridian,
  caption,
  exaggeration = DEFAULT_EXAGGERATION,
  size = FIGURE_SIZE,
  showLock = true,
}: TargetFigureType) => {
  const { t } = useTranslation();

  const perDegree = useMemo(
    () => (geometry ? size / (2 * geometry.fieldExtentDegrees) : 0),
    [geometry, size]
  );

  const halves = useMemo(() => {
    if (!geometry || !view) return null;

    const stage = geometry.meridians?.[meridian];
    if (!stage) return null;

    const toPx = (degrees: number) => degrees * perDegree;
    const centre = size / 2;

    // Raising the ratio to a power exaggerates the difference without moving the pair's mean
    // size, which is what keeps the two halves comparable. It is taken against this stage's own
    // unscaled extent, so a bar pair is exaggerated on the axis it is actually judged on.
    const exaggerate = (actual: number, unscaled: number) => {
      if (!unscaled || !Number.isFinite(actual)) return actual;
      return unscaled * Math.pow(actual / unscaled, exaggeration);
    };

    const toShape = (half: AniseikoniaHalfViewType) => ({
      width: toPx(exaggerate(half.widthDegrees, stage.featureWidthDegrees)),
      height: toPx(exaggerate(half.heightDegrees, stage.featureHeightDegrees)),
      cx: centre + toPx(half.offsetXDegrees),
      cy: centre + toPx(half.offsetYDegrees),
      flatEdgeOnRight: half.flatEdgeOnRight,
    });

    return { left: toShape(view.left), right: toShape(view.right) };
  }, [geometry, view, meridian, perDegree, size, exaggeration]);

  // The central fixation cross, the second fusion aid. Drawn at life size and NOT through
  // `exaggerate`: it carries no interocular difference, it is identical in both eyes, and the
  // patient's control does not scale it -- so exaggerating it would grow a fiducial as though it
  // were part of the comparison. Only when the geometry carries it, so a report from a headset
  // built before the cross existed draws the field it actually showed.
  const cross = useMemo(() => {
    if (!geometry?.fixationArmDegrees || !geometry?.fixationThicknessDegrees) {
      return null;
    }

    return {
      arm: geometry.fixationArmDegrees * perDegree,
      thickness: geometry.fixationThicknessDegrees * perDegree,
    };
  }, [geometry, perDegree]);

  if (!geometry || !view || !halves) {
    return (
      <Typography fontSize="11px" color="#5B6D7A">
        {t("No Data Available.")}
      </Typography>
    );
  }

  const fill = `rgba(255, 255, 255, ${geometry.luminanceFraction})`;

  const renderHalf = (
    shape: NonNullable<typeof halves>["left"],
    key: string
  ) => {
    if (geometry.primitive === "Bar") {
      return (
        <rect
          key={key}
          data-testid={`aniseikonia-half-${key}`}
          x={shape.cx - shape.width / 2}
          y={shape.cy - shape.height / 2}
          width={shape.width}
          height={shape.height}
          fill={fill}
        />
      );
    }

    // A half disc: the full ellipse with the half on `flatEdgeOnRight` removed, so the straight
    // edge faces the fusion centre.
    const clipId = `aniseikonia-clip-${key}`;
    return (
      <g key={key}>
        <defs>
          <clipPath id={clipId}>
            <rect
              x={shape.flatEdgeOnRight ? shape.cx - shape.width / 2 : shape.cx}
              y={shape.cy - shape.height / 2}
              width={shape.width / 2}
              height={shape.height}
            />
          </clipPath>
        </defs>
        <ellipse
          data-testid={`aniseikonia-half-${key}`}
          cx={shape.cx}
          cy={shape.cy}
          rx={shape.width / 2}
          ry={shape.height / 2}
          fill={fill}
          clipPath={`url(#${clipId})`}
        />
      </g>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="4px" alignItems="center">
      <Box
        component="svg"
        role="img"
        data-testid="aniseikonia-target-figure"
        aria-label={t("What the Patient Saw")}
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        sx={{ display: "block", borderRadius: "4px", background: "#0B0D14" }}
      >
        {showLock && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={
              ((geometry.lockInnerRadiusDegrees +
                geometry.lockOuterRadiusDegrees) /
                2) *
              perDegree
            }
            fill="none"
            stroke="rgba(255, 255, 255, 0.22)"
            strokeWidth={
              (geometry.lockOuterRadiusDegrees -
                geometry.lockInnerRadiusDegrees) *
              perDegree
            }
          />
        )}
        {showLock && cross && (
          <g data-testid="aniseikonia-fixation-cross">
            <rect
              data-testid="aniseikonia-fixation-arm-horizontal"
              x={size / 2 - cross.arm / 2}
              y={size / 2 - cross.thickness / 2}
              width={cross.arm}
              height={cross.thickness}
              fill={fill}
            />
            <rect
              data-testid="aniseikonia-fixation-arm-vertical"
              x={size / 2 - cross.thickness / 2}
              y={size / 2 - cross.arm / 2}
              width={cross.thickness}
              height={cross.arm}
              fill={fill}
            />
          </g>
        )}
        {renderHalf(halves.left, "left")}
        {renderHalf(halves.right, "right")}
      </Box>
      <Typography
        fontSize="10px"
        color="#5B6D7A"
        textAlign="center"
        sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.35 }}
      >
        {caption ? `${caption} — ` : ""}
        {t("Sizes Exaggerated for Display")} (&times;{exaggeration})
      </Typography>
    </Box>
  );
};

export default TargetFigure;
